import React, { useState, useEffect, useMemo } from 'react';
import { auditLogsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Search, History, Filter, ShieldAlert, User as UserIcon, Cpu, Trash2,
  Eye, Info, Calendar as CalendarIcon,
} from 'lucide-react';
import { formatDateTime } from '../lib/utils';

// Actions that MUST stand out — anyone auditing a security incident wants
// these bright and obvious. System startup/shutdown events are informational
// (not privileged actions), so they get their own slate style below.
const SENSITIVE_ACTIONS = new Set([
  'SSN_VIEWED', 'BANK_ACCOUNT_VIEWED', 'COMPENSATION_VIEWED',
  'HARD_DELETE', 'RESET_PASSWORD', 'CHANGE_PASSWORD',
  'CREATE_ROLE', 'UPDATE_ROLE', 'DELETE_ROLE', 'UPDATE_ACCESS',
  'DENY', 'LOGIN_FAILED',
]);

const RANGE_PRESETS = [
  { key: '24h', label: 'Last 24 hours', hours: 24 },
  { key: '7d', label: 'Last 7 days', hours: 24 * 7 },
  { key: '30d', label: 'Last 30 days', hours: 24 * 30 },
  { key: '90d', label: 'Last 90 days', hours: 24 * 90 },
  { key: 'all', label: 'All time', hours: null },
];

// Turn `log.details` (a small JSON blob) into a compact, human-readable
// sentence. Falls back to raw JSON if we don't know the shape.
function renderDetails(log) {
  const d = log.details || {};
  const parts = [];
  if (d.employee_name) parts.push(<span key="en"><b>{d.employee_name}</b></span>);
  if (d.email) parts.push(<span key="em">{d.email}</span>);
  if (d.customer_number) parts.push(<span key="cn" className="font-mono text-xs">#{d.customer_number}</span>);
  if (d.role) parts.push(<span key="r">role=<b>{d.role}</b></span>);
  if (d.target_email) parts.push(<span key="te">target: {d.target_email}</span>);
  if (d.job_number) parts.push(<span key="jn" className="font-mono text-xs">{d.job_number}</span>);
  if (d.name && !d.employee_name) parts.push(<span key="n">{d.name}</span>);
  if (d.subject) parts.push(<span key="s">&ldquo;{d.subject}&rdquo;</span>);
  if (d.reason && !parts.length) parts.push(<span key="rs">reason: {d.reason}</span>);
  if (typeof d.is_active === 'boolean') parts.push(<span key="ia">active={String(d.is_active)}</span>);
  if (parts.length) {
    return <span className="flex flex-wrap gap-x-2 gap-y-0.5">{parts.reduce((a, p, i) => (i ? [...a, <span key={`s${i}`} className="text-slate-300">·</span>, p] : [p]), [])}</span>;
  }
  const raw = JSON.stringify(d);
  return raw === '{}' ? <span className="text-slate-300">—</span> : <span className="font-mono text-xs">{raw}</span>;
}

function actionColor(action) {
  if (SENSITIVE_ACTIONS.has(action)) return 'bg-red-100 text-red-700 border-red-200';
  if (action?.startsWith('SYSTEM_')) return 'bg-slate-200 text-slate-700 border-slate-300';
  const map = {
    CREATE: 'bg-emerald-100 text-emerald-700',
    UPDATE: 'bg-blue-100 text-blue-700',
    DELETE: 'bg-red-100 text-red-700',
    APPROVE: 'bg-green-100 text-green-700',
    REJECT: 'bg-red-100 text-red-700',
    LOGIN: 'bg-indigo-100 text-indigo-700',
    ISSUE: 'bg-purple-100 text-purple-700',
    RECEIVE: 'bg-teal-100 text-teal-700',
    ADJUST: 'bg-amber-100 text-amber-700',
    PREVIEW: 'bg-slate-100 text-slate-700',
    APPLY: 'bg-blue-100 text-blue-700',
    ACTIVATE: 'bg-emerald-100 text-emerald-700',
    DEACTIVATE: 'bg-slate-200 text-slate-700',
  };
  return map[action] || 'bg-slate-100 text-slate-700';
}

export default function AuditLogs() {
  const { user: currentUser } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [actorFilter, setActorFilter] = useState('all');
  const [rangeKey, setRangeKey] = useState('30d');
  const [gapWarning, setGapWarning] = useState(null);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 2000 };
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      const preset = RANGE_PRESETS.find(r => r.key === rangeKey);
      if (preset?.hours) {
        params.from_date = new Date(Date.now() - preset.hours * 3600 * 1000).toISOString();
      }
      const response = await auditLogsAPI.list(params);
      const rows = response.data || [];
      setLogs(rows);
      detectGap(rows);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Detect a suspicious gap: any span >4 hours during business hours with no
  // entries at all. This is the mechanism that would have surfaced the
  // July-9 gap in real time.
  const detectGap = (rows) => {
    if (rows.length < 2) { setGapWarning(null); return; }
    let biggestGapMs = 0;
    let gapAround = null;
    for (let i = 0; i < rows.length - 1; i++) {
      const a = new Date(rows[i].timestamp).getTime();
      const b = new Date(rows[i + 1].timestamp).getTime();
      const delta = a - b; // rows are sorted DESC
      if (delta > biggestGapMs) {
        biggestGapMs = delta;
        gapAround = { after: rows[i + 1].timestamp, before: rows[i].timestamp };
      }
    }
    const hrs = biggestGapMs / (3600 * 1000);
    if (hrs > 96) {
      setGapWarning({ hours: Math.round(hrs), ...gapAround });
    } else {
      setGapWarning(null);
    }
  };

  useEffect(() => { loadLogs(); }, [entityFilter, rangeKey]);

  const entityTypes = useMemo(() => {
    return [...new Set(logs.map(l => l.entity_type).filter(Boolean))].sort();
  }, [logs]);

  const actors = useMemo(() => {
    const map = new Map();
    logs.forEach(l => {
      if (!l.user_id) return;
      if (!map.has(l.user_id)) {
        map.set(l.user_id, { id: l.user_id, name: l.user_name || '(deleted user)', email: l.user_email || '' });
      }
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [logs]);

  const filteredLogs = useMemo(() => {
    const q = search.toLowerCase().trim();
    return logs.filter(log => {
      if (actorFilter !== 'all' && log.user_id !== actorFilter) return false;
      if (!q) return true;
      return (
        log.action?.toLowerCase().includes(q) ||
        log.entity_type?.toLowerCase().includes(q) ||
        log.entity_id?.toLowerCase().includes(q) ||
        log.user_name?.toLowerCase().includes(q) ||
        log.user_email?.toLowerCase().includes(q) ||
        JSON.stringify(log.details || {}).toLowerCase().includes(q)
      );
    });
  }, [logs, search, actorFilter]);

  const sensitiveCount = filteredLogs.filter(l => SENSITIVE_ACTIONS.has(l.action)).length;

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Audit Logs</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Every write and every sensitive read is captured here. Newest first.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Entries loaded</p>
            <p className="text-2xl font-semibold">{logs.length}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Sensitive</p>
            <p className="text-2xl font-semibold text-red-600" data-testid="audit-sensitive-count">{sensitiveCount}</p>
          </div>
        </div>
      </div>

      {/* Gap warning */}
      {gapWarning && (
        <Card className="border-amber-300 bg-amber-50" data-testid="audit-gap-warning">
          <CardContent className="p-4 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900">
                Detected a {gapWarning.hours}-hour gap in the audit timeline
              </p>
              <p className="text-sm text-amber-800 mt-1">
                No entries were recorded between{' '}
                <b>{formatDateTime(gapWarning.after)}</b> and{' '}
                <b>{formatDateTime(gapWarning.before)}</b>. Legitimate causes: platform maintenance,
                a very quiet weekend. Concerning causes: an attacker cleared logs after gaining
                access. Look for a <code className="bg-surface px-1 rounded">SYSTEM_STARTUP</code>{' '}
                entry at the boundary — its presence means a redeploy; its absence needs review.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search action, entity, actor, or details…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="audit-search"
            />
          </div>
          <Select value={rangeKey} onValueChange={setRangeKey}>
            <SelectTrigger className="w-[170px]" data-testid="audit-range-filter">
              <CalendarIcon className="w-4 h-4 mr-1.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_PRESETS.map(r => (
                <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-[170px]" data-testid="audit-entity-filter">
              <Filter className="w-4 h-4 mr-1.5" />
              <SelectValue placeholder="Entity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              {entityTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={actorFilter} onValueChange={setActorFilter}>
            <SelectTrigger className="w-[220px]" data-testid="audit-actor-filter">
              <UserIcon className="w-4 h-4 mr-1.5" />
              <SelectValue placeholder="Actor" />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="all">All actors</SelectItem>
              {actors.map(a => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name} {a.email && <span className="text-xs text-slate-400">({a.email})</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        {loading ? (
          <div className="p-8 space-y-3">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-10 skeleton-pulse rounded" />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">When</TableHead>
                  <TableHead className="w-[160px]">Action</TableHead>
                  <TableHead className="w-[220px]">Actor</TableHead>
                  <TableHead className="w-[130px]">Entity</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map(log => {
                  const isMe = currentUser && log.user_id === currentUser.id && log.actor_kind !== 'public_signer';
                  // Non-human actors: a scheduled loop, a migration, a webhook,
                  // a login attempt, or an employee signing via a public link.
                  const nonHuman = log.actor_kind && log.actor_kind !== 'user';
                  const isSystem = log.user_id === 'system' || ['system', 'migration', 'webhook', 'cli'].includes(log.actor_kind);
                  const unattributed = log.actor_kind === 'unattributed' || log.user_name === 'unattributed';
                  const isSensitive = SENSITIVE_ACTIONS.has(log.action);
                  return (
                    <TableRow
                      key={log.id}
                      data-testid={`log-row-${log.id}`}
                      className={isSensitive ? 'bg-red-50/40' : ''}
                    >
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDateTime(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${actionColor(log.action)} border`}>
                          {isSensitive && <ShieldAlert className="w-3 h-3 mr-1 inline" />}
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5">
                            {isSystem ? (
                              <Cpu className="w-3.5 h-3.5 text-slate-500" />
                            ) : (
                              <UserIcon className="w-3.5 h-3.5 text-slate-500" />
                            )}
                            <span className={`font-medium text-sm ${unattributed ? 'italic text-slate-400' : ''}`}
                              data-testid={`log-actor-${log.id}`}>{log.user_name}</span>
                            {nonHuman && !isSystem && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 border-slate-300 text-slate-600">
                                {log.actor_kind.replace('_', ' ')}
                              </Badge>
                            )}
                            {isMe && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                                you
                              </Badge>
                            )}
                          </div>
                          {log.user_email && (
                            <span className="text-xs text-slate-400 truncate max-w-[200px]">{log.user_email}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="capitalize text-sm">{log.entity_type || '—'}</span>
                          {log.entity_id && log.entity_id !== 'backend' && (
                            <span className="font-mono text-[10px] text-slate-400 truncate max-w-[110px]" title={log.entity_id}>
                              {log.entity_id.slice(0, 12)}{log.entity_id.length > 12 ? '…' : ''}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-md">
                        {renderDetails(log)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredLogs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                      <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No audit logs match these filters.</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Legend */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-4 text-xs text-slate-600 flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5 text-red-600" /> <span>Sensitive read / privileged change</span></div>
          <div className="flex items-center gap-1.5"><Cpu className="w-3.5 h-3.5 text-slate-500" /> <span>System / startup event</span></div>
          <div className="flex items-center gap-1.5"><Info className="w-3.5 h-3.5 text-slate-400" /> <span>A gap &gt; 4 days will trigger the warning above</span></div>
        </CardContent>
      </Card>
    </div>
  );
}
